import { isTotalCostOfOwnershipEnabled } from '@/featureFlags';
import { useHttpMutation } from '@/resources/mutation/useHttpMutation';
import { t, Trans } from '@lingui/macro';
import {
    assertUnreachable,
    Currency,
    formatMonetaryValue,
    isPresent,
    MonetaryValue,
    transEnum,
} from '@luminovo/commons';
import {
    Chip,
    colorSystem,
    DestructiveSecondaryIconButton,
    DestructiveTertiaryIconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FieldCheckboxControlled,
    FieldNumericControlled,
    FieldSelectControlled,
    FieldTextControlled,
    FieldToggleButtonControlled,
    Flexbox,
    FormItem,
    Message,
    SecondaryButton,
    Tab,
    Tabs,
    Tag,
    TertiaryButton,
    Text,
} from '@luminovo/design-system';
import { ManualTcoConfigurationDTO, OneTimeCostDTO, SolutionConfigurationDTO } from '@luminovo/http-client';
import {
    CostType,
    getSolutionSelectionType,
    getTcoColor,
    hasSolutionConfigurationManualCost,
    Solution,
    SolutionSelectionType,
    tcoCategoryTranslations,
} from '@luminovo/sourcing-core';
import { Add, CloseRounded, Delete, Edit, ExpandMore } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionProps,
    AccordionSummary,
    AccordionSummaryProps,
    Box,
    InputAdornment,
    styled,
} from '@mui/material';
import React from 'react';
import { SubmitHandler, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useDialogContext } from '../../../../components/contexts/ModalContext';
import { CancelButton } from '../../../../components/formLayouts/CancelButton';
import { FormContainer } from '../../../../components/formLayouts/FormContainer';
import { SubmitButton } from '../../../../components/formLayouts/SubmitButton';
import { inputCurrenciesPublicTranslations } from '../../../../resources/currencyInputTypes';
import { useGlobalCurrency } from '../../../../resources/organizationSettings/currencySettingsHandler';

type FormState = {
    oneTimeCosts: OneTimeCostDTO[];
    tcoCosts: Record<CostType, TcoCostItem>;
};

type TcoCostItem = {
    includeAutomaticCosts: boolean;
    manualCosts: {
        name: string;
        actionType: 'Relative' | 'Absolute';
        actionRelativeValue: number;
        actionAbsoluteValue: MonetaryValue;
    }[];
};

function ManualCostsLineItem({
    type,
    index,
    remove,
}: {
    type: CostType;
    index: number;
    remove: (index: number) => void;
}) {
    const { control } = useFormContext<FormState>();
    const currency = useWatch({ control, name: `tcoCosts.${type}.manualCosts.${index}.actionAbsoluteValue.currency` });
    const actionType = useWatch({ control, name: `tcoCosts.${type}.manualCosts.${index}.actionType` });

    return (
        <React.Fragment>
            <FieldTextControlled
                required={true}
                control={control}
                name={`tcoCosts.${type}.manualCosts.${index}.name`}
                FieldProps={{ size: 'small', placeholder: t`Name` }}
            />
            <FieldToggleButtonControlled
                control={control}
                name={`tcoCosts.${type}.manualCosts.${index}.actionType`}
                FieldProps={{
                    options: ['Relative', 'Absolute'] as const,
                    getOptionLabel: (option) => {
                        if (option === 'Relative') {
                            return t`Percentage`;
                        } else {
                            return t`Absolute`;
                        }
                    },
                }}
            />
            {actionType === 'Absolute' && (
                <>
                    <FieldNumericControlled
                        control={control}
                        name={`tcoCosts.${type}.manualCosts.${index}.actionAbsoluteValue.amount`}
                        required={true}
                        min={0}
                        isFinite={true}
                        FieldProps={{
                            fullWidth: true,
                            size: 'small',
                            InputProps: {
                                endAdornment: <InputAdornment position="end">{currency}</InputAdornment>,
                            },
                        }}
                    />
                    <FieldSelectControlled
                        control={control}
                        name={`tcoCosts.${type}.manualCosts.${index}.actionAbsoluteValue.currency`}
                        required={true}
                        FieldProps={{
                            options: Object.values(Currency),
                            getOptionLabel: (option) => transEnum(option, inputCurrenciesPublicTranslations),
                            disableClearable: true,
                            size: 'small',
                            style: { width: '100px' },
                        }}
                    />
                </>
            )}
            {actionType === 'Relative' && (
                <>
                    <FieldNumericControlled
                        control={control}
                        name={`tcoCosts.${type}.manualCosts.${index}.actionRelativeValue`}
                        required={true}
                        min={0}
                        max={1_000}
                        FieldProps={{
                            fullWidth: true,
                            size: 'small',
                            InputProps: {
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            },
                        }}
                    />
                    <span style={{ width: '100px' }} />
                </>
            )}
            <DestructiveTertiaryIconButton onClick={() => remove(index)} size="medium">
                <CloseRounded fontSize="inherit" />
            </DestructiveTertiaryIconButton>
        </React.Fragment>
    );
}

function extractAutomaticTcoCost(type: CostType, solution: Solution) {
    const breakdown = solution.cost.tco_costs?.breakdown;

    switch (type) {
        case 'packaging':
            return breakdown?.packaging_cost?.automatic_cost;
        case 'discount':
            return breakdown?.discount?.automatic_cost;
        case 'shipping':
            return breakdown?.shipping_cost?.automatic_cost;
        case 'customs':
            return breakdown?.customs_cost?.automatic_cost;
        case 'other':
            return breakdown?.other_cost?.automatic_cost;
        default:
            assertUnreachable(type);
    }
}

function AutomaticTcoCostTag({ type, solution }: { type: CostType; solution: Solution }) {
    const automaticCost = extractAutomaticTcoCost(type, solution);
    const color = getTcoColor(type);

    return <Tag color={color} attention="low" label={formatMonetaryValue(automaticCost?.preferred, 'unit-price')} />;
}

const TcoCostAccordion = styled((props: AccordionProps) => (
    <Accordion disableGutters elevation={0} square {...props} />
))(() => ({
    borderRadius: '8px',
    border: `1px solid ${colorSystem.neutral[3]}`,
    boxShadow: 'none',
    '&::before': {
        display: 'none',
    },
    '&.Mui-expanded': {
        border: `1px solid ${colorSystem.neutral[3]}`,
        boxShadow: '0px 4px 8px rgba(158, 158, 158, 0.15)',
    },
}));

const TcoCostAccordionSummary = styled((props: AccordionSummaryProps) => <AccordionSummary {...props} />)(() => ({
    backgroundColor: colorSystem.neutral[0],
}));

const TcoCostAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    borderTop: `1px solid ${colorSystem.neutral[3]}`,
}));

function FormItemManualCosts({ type, solution }: { type: CostType; solution: Solution }) {
    const { preferredCurrency } = useGlobalCurrency();
    const { control } = useFormContext<FormState>();
    const { fields, append, remove } = useFieldArray({ control, name: `tcoCosts.${type}.manualCosts` });
    const includeAutomaticCosts = useWatch({ control, name: `tcoCosts.${type}.includeAutomaticCosts` });

    const hasManualTcoCosts = fields.length > 0;
    const isExpanded = !includeAutomaticCosts || hasManualTcoCosts;

    return (
        <TcoCostAccordion defaultExpanded={isExpanded} slotProps={{ root: { sx: { overflow: 'hidden' } } }}>
            <TcoCostAccordionSummary expandIcon={<ExpandMore />}>
                <Flexbox justifyContent="space-between" width="100%" padding="0 10px 0 0">
                    <Text variant="h4">{transEnum(type, tcoCategoryTranslations)}</Text>
                </Flexbox>
            </TcoCostAccordionSummary>
            <TcoCostAccordionDetails style={{ paddingTop: '20px', paddingBottom: '20px' }}>
                <Flexbox flexDirection="column" gap="12px">
                    <Flexbox justifyContent="space-between" width="100%">
                        <Flexbox gap={8} alignItems={'center'}>
                            <FieldCheckboxControlled
                                control={control}
                                name={`tcoCosts.${type}.includeAutomaticCosts`}
                                FieldProps={{ size: 'small' }}
                            />
                            <Text variant="h5" color={colorSystem.neutral[8]}>
                                <Trans>Include costs from TCO rules:</Trans>
                            </Text>
                            <AutomaticTcoCostTag type={type} solution={solution} />
                        </Flexbox>
                        <SecondaryButton
                            startIcon={<Add />}
                            onClick={() =>
                                append({
                                    name: '',
                                    actionType: 'Relative',
                                    actionRelativeValue: 0,
                                    actionAbsoluteValue: { amount: '0', currency: preferredCurrency },
                                })
                            }
                            size="small"
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            <Trans>Add manual cost</Trans>
                        </SecondaryButton>
                    </Flexbox>
                    {fields.length === 0 && !includeAutomaticCosts && (
                        <Box padding="10px 0">
                            <Message
                                variant="primary"
                                size="small"
                                attention="low"
                                message={t`TCO costs from rules are not added to this category`}
                            />
                        </Box>
                    )}

                    {fields.length > 0 && (
                        <Box
                            display={'grid'}
                            alignItems="top"
                            gridTemplateColumns={'3fr 1fr 2fr 1fr auto'}
                            rowGap={'16px'}
                            columnGap={'16px'}
                            padding={'12px 0'}
                        >
                            {fields.map((item, index) => (
                                <ManualCostsLineItem
                                    type={type}
                                    index={index}
                                    remove={remove}
                                    key={`${index}-${JSON.stringify(item)}}`}
                                />
                            ))}
                        </Box>
                    )}
                </Flexbox>
            </TcoCostAccordionDetails>
        </TcoCostAccordion>
    );
}

function generateDefaultValues({
    solutionConfiguration,
    preferredCurrency,
    offerCurrency,
}: {
    solutionConfiguration: SolutionConfigurationDTO;
    preferredCurrency: Currency;
    offerCurrency: Currency;
}): FormState {
    const convert = (input: ManualTcoConfigurationDTO | undefined | null) => {
        if (!isPresent(input)) {
            return {
                includeAutomaticCosts: true,
                manualCosts: [],
            };
        }

        return {
            includeAutomaticCosts: input.include_automatic_costs,
            manualCosts: input.manual_costs.map((item) => ({
                name: item.name,
                actionType: item.action.type,
                actionRelativeValue: item.action.type === 'Relative' ? Number(item.action.value) : 0,
                actionAbsoluteValue:
                    item.action.type === 'Absolute' ? item.action.value : { amount: '0', currency: preferredCurrency },
            })),
        };
    };

    return {
        oneTimeCosts:
            solutionConfiguration.manual_one_time_costs && hasSolutionConfigurationManualCost(solutionConfiguration)
                ? solutionConfiguration.manual_one_time_costs
                : [{ price: { amount: '0', currency: offerCurrency }, description: '' }],
        tcoCosts: {
            packaging: convert(solutionConfiguration.manual_total_cost_of_ownership?.packaging),
            discount: convert(solutionConfiguration.manual_total_cost_of_ownership?.discount),
            shipping: convert(solutionConfiguration.manual_total_cost_of_ownership?.shipping),
            customs: convert(solutionConfiguration.manual_total_cost_of_ownership?.customs),
            other: convert(solutionConfiguration.manual_total_cost_of_ownership?.other),
        },
    };
}

const OneTimeCostItem: React.FunctionComponent<{ index: number; onClickDelete: () => void }> = ({
    index,
    onClickDelete,
}): JSX.Element => {
    const { control } = useFormContext<FormState>();

    const currency = useWatch({
        control,
        name: `oneTimeCosts.${index}.price.currency`,
    });

    return (
        <Flexbox flexDirection="column" border={`1px solid ${colorSystem.neutral[4]}`} borderRadius={'4px'}>
            <Flexbox
                flexDirection="row"
                justifyContent={'space-between'}
                bgcolor={colorSystem.neutral[0]}
                padding={'12px'}
                borderBottom={`1px solid ${colorSystem.neutral[4]}`}
                borderRadius={'4px 4px 0 0'}
            >
                <Text variant={'h3'} color={colorSystem.neutral[8]}>
                    <Trans>One time cost</Trans>
                </Text>
                <DestructiveSecondaryIconButton size={'small'} onClick={onClickDelete}>
                    <Delete fontSize="inherit" />
                </DestructiveSecondaryIconButton>
            </Flexbox>
            <Flexbox flexDirection="row" justifyContent={'space-between'} padding={'12px'}>
                <FormItem label={t`Cost`} required>
                    <Flexbox flexDirection="row" gap={8}>
                        <FieldNumericControlled
                            control={control}
                            name={`oneTimeCosts.${index}.price.amount`}
                            required
                            min={0}
                            isFinite={true}
                            FieldProps={{
                                InputProps: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {transEnum(currency, inputCurrenciesPublicTranslations)}
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <FieldSelectControlled
                            control={control}
                            name={`oneTimeCosts.${index}.price.currency`}
                            required
                            FieldProps={{
                                options: Object.values(Currency),
                                getOptionLabel: (option) => transEnum(option, inputCurrenciesPublicTranslations),
                                disableClearable: true,
                            }}
                        />
                    </Flexbox>
                </FormItem>

                <FormItem label={t`Description`} style={{ width: '50%' }}>
                    <FieldTextControlled
                        control={control}
                        name={`oneTimeCosts.${index}.description`}
                        FieldProps={{ style: { width: '100%' } }}
                    />
                </FormItem>
            </Flexbox>
        </Flexbox>
    );
};

const ManualCostFormInner: React.FunctionComponent<{ offerCurrency: Currency }> = ({ offerCurrency }): JSX.Element => {
    const { control } = useFormContext<FormState>();

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'oneTimeCosts',
    });

    return (
        <Flexbox flexDirection="column" gap={12}>
            <Flexbox overflow={'auto'} flexDirection="column" gap={12}>
                {fields.map((_, index) => (
                    <OneTimeCostItem
                        key={`${index}-${fields.length}`}
                        index={index}
                        onClickDelete={() => remove(index)}
                    />
                ))}
            </Flexbox>
            <SecondaryButton
                startIcon={<Add />}
                disabled={fields.length >= 10}
                onClick={() => append({ price: { amount: '0', currency: offerCurrency }, description: '' })}
                style={{ width: 'fit-content' }}
            >
                <Trans>Add another cost</Trans>
            </SecondaryButton>
        </Flexbox>
    );
};

const TcoCostFormInner: React.FunctionComponent<{ solution: Solution }> = ({ solution }): JSX.Element => {
    return (
        <Flexbox flexDirection="column" gap={12}>
            <FormItemManualCosts type="packaging" solution={solution} />
            <FormItemManualCosts type="discount" solution={solution} />
            <FormItemManualCosts type="shipping" solution={solution} />
            <FormItemManualCosts type="customs" solution={solution} />
            <FormItemManualCosts type="other" solution={solution} />
        </Flexbox>
    );
};

function InnerContent({ solution, offerCurrency }: { solution: Solution; offerCurrency: Currency }) {
    type CostTabsValue = 'one-time-cost-tab' | 'total-cost-of-ownership-tab';
    const [tab, setTab] = React.useState<CostTabsValue>('one-time-cost-tab');

    return (
        <DialogContent sx={{ height: '77vh' }}>
            <Tabs size={'large'} value={tab} onChange={(_, value) => setTab(value)} sx={{ marginBottom: '12px' }}>
                <Tab value={'one-time-cost-tab'} label={t`One-time costs`} />
                {isTotalCostOfOwnershipEnabled() && <Tab value={'total-cost-of-ownership-tab'} label={t`TCO costs`} />}
            </Tabs>
            <Box sx={{ overflow: 'auto', marginTop: '12px', height: 'calc(100% - 48px)' }}>
                <Box paddingBottom="12px">
                    <Text variant={'body'} color={colorSystem.neutral[8]}>
                        <Trans>
                            You can manually add additional costs to the selected solution. The manually added costs
                            will only be applied to this sourcing scenario.
                        </Trans>
                    </Text>
                </Box>
                {tab === 'one-time-cost-tab' && <ManualCostFormInner offerCurrency={offerCurrency} />}
                {tab === 'total-cost-of-ownership-tab' && <TcoCostFormInner solution={solution} />}
            </Box>
        </DialogContent>
    );
}

function ManualCostDialog({
    solution,
    solutionConfiguration,
    preferredCurrency,
    closeDialog,
    onSubmit,
}: {
    solution: Solution;
    solutionConfiguration: SolutionConfigurationDTO;
    preferredCurrency: Currency;
    closeDialog: () => void;
    onSubmit: SubmitHandler<FormState>;
}): JSX.Element {
    const offerCurrency = solution.offerUnitPrice?.original.currency ?? Currency.EUR;
    const defaultValues = generateDefaultValues({ solutionConfiguration, preferredCurrency, offerCurrency });

    return (
        <Dialog open={true} maxWidth="md" onClose={() => closeDialog()}>
            <FormContainer defaultValues={defaultValues} onSubmit={onSubmit}>
                <DialogTitle title={t`Add additional costs`} sticky={true} onClose={() => closeDialog()} />
                <DialogContent>
                    <InnerContent solution={solution} offerCurrency={offerCurrency} />
                </DialogContent>
                <DialogActions sticky={true}>
                    <CancelButton onClick={() => closeDialog()} />
                    <SubmitButton />
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
}

function useManualAdditionalCostDialog(solutionConfiguration: SolutionConfigurationDTO, solution: Solution) {
    const { setDialog, closeDialog } = useDialogContext();
    const { preferredCurrency } = useGlobalCurrency();

    const { mutateAsync } = useHttpMutation('PATCH /solution-configurations/:solutionConfigurationId', {
        snackbarMessage: t`Manual additional costs updated`,
        onSuccess: () => closeDialog(),
    });

    const onSubmit = async (formValues: FormState) => {
        const convert = (input: TcoCostItem | undefined | null) => {
            if (!isPresent(input)) {
                return null;
            }

            if (input.includeAutomaticCosts && input.manualCosts.length === 0) {
                return null;
            }

            return {
                include_automatic_costs: input.includeAutomaticCosts,
                manual_costs: input.manualCosts.map((item) => ({
                    name: item.name,
                    action:
                        item.actionType === 'Relative'
                            ? { type: 'Relative' as const, value: String(item.actionRelativeValue) }
                            : { type: 'Absolute' as const, value: item.actionAbsoluteValue },
                })),
            };
        };

        const solutionToken =
            getSolutionSelectionType(solution) === SolutionSelectionType.AUTO_SELECTED ? solution.token : undefined;

        const tcoConfig = {
            packaging: convert(formValues.tcoCosts.packaging),
            discount: convert(formValues.tcoCosts.discount),
            shipping: convert(formValues.tcoCosts.shipping),
            customs: convert(formValues.tcoCosts.customs),
            other: convert(formValues.tcoCosts.other),
        };

        // Check if all TCO costs are null
        const allOneTimeCostsNull = formValues.oneTimeCosts.length === 0;
        const allTcoCostsNull = Object.values(tcoConfig).every((value) => value === null);

        await mutateAsync({
            pathParams: { solutionConfigurationId: solutionConfiguration.id },
            requestBody: {
                manual_one_time_costs: allOneTimeCostsNull ? null : formValues.oneTimeCosts,
                manual_total_cost_of_ownership: allTcoCostsNull ? null : tcoConfig,
                manually_selected_solution_token: solutionToken,
            },
        });
    };
    return {
        openDialog: () =>
            setDialog(
                <ManualCostDialog
                    solution={solution}
                    solutionConfiguration={solutionConfiguration}
                    preferredCurrency={preferredCurrency}
                    closeDialog={closeDialog}
                    onSubmit={onSubmit}
                />,
            ),
    };
}

export const ManualAdditionalCostButton: React.FunctionComponent<{
    solutionConfiguration: SolutionConfigurationDTO;
    solution: Solution;
}> = ({ solutionConfiguration, solution }): JSX.Element => {
    const { openDialog } = useManualAdditionalCostDialog(solutionConfiguration, solution);
    const hasOneTimeCost = isPresent(solutionConfiguration.manual_one_time_costs);
    const hasTcoCosts =
        isTotalCostOfOwnershipEnabled() && isPresent(solutionConfiguration.manual_total_cost_of_ownership);

    if (hasOneTimeCost || hasTcoCosts) {
        return (
            <Flexbox alignItems="center" justifyContent={'space-between'} style={{ width: '100%' }}>
                <Chip label={t`Additional costs added`} color={'neutral'} />
                <TertiaryButton size="small" startIcon={<Edit />} onClick={() => openDialog()}>
                    <Trans>Edit</Trans>
                </TertiaryButton>
            </Flexbox>
        );
    } else {
        return (
            <TertiaryButton size="small" startIcon={<Add />} onClick={() => openDialog()}>
                <Trans>Add additional costs</Trans>
            </TertiaryButton>
        );
    }
};
